<template>
  <SectionCard :id="section.id" :title="section.title">
    <div class="grid gap-6">
      <div>
        <AppButton
          small
          color="blue"
          :loading="isKalpLoading"
          :disabled="!kalp"
          :title="kalpError || undefined"
          @click="openKalpModal"
        >
          Visa KALP-beräkning
        </AppButton>
      </div>
      <!-- Låneöversikt -->
      <div :id="section.subSections.overview.id">
        <AppTable>
          <thead>
            <tr>
              <th>Ansökt belopp</th>
              <th>Beviljat belopp</th>
              <th>Lägsta amortering</th>
              <th>Belåningsgrad</th>
              <th>Bostadens värde</th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow>
              <AppTableCell>{{ formatAmountWithCurrency(application.amountAppliedFor) }}</AppTableCell>
              <AppTableCell>{{ formatAmountWithCurrency(application.amountApproved) }}</AppTableCell>
              <AppTableCell>
                <template v-if="application.minAmortization != null">
                  {{ formatAmountWithCurrency(application.minAmortization) }}/mån
                </template>
                <span v-else class="text-subtle">-</span>
              </AppTableCell>
              <AppTableCell>
                <template v-if="ltvPercentage != null">{{ ltvPercentage.toFixed(2) }} %</template>
                <span v-else class="text-subtle">-</span>
              </AppTableCell>
              <AppTableCell>
                {{ formatAmountWithCurrency(propertyValue || undefined) }}
              </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
      </div>

      <!-- Amorteringsunderlag -->
      <div v-if="application.type === 'transferLoanApplication'">
        <h3 :id="section.subSections.amortizationProof.id" class="mb-2">
          {{ section.subSections.amortizationProof.title }}
        </h3>
        <AppTable>
          <thead>
            <tr>
              <th>Dokumenttyp</th>
              <th>Uppladdningsdatum</th>
              <th></th>
            </tr>
          </thead>
          <tbody v-if="amortizationProofs?.length">
            <AppTableRow v-for="doc in amortizationProofs" :key="doc.id">
              <AppTableCell>{{ translateDocumentType(doc.type) }}</AppTableCell>
              <AppTableCell nowrap>{{ formatDateIfValid(doc.createdAt) }}</AppTableCell>
              <AppTableCell action>
                <AppButton
                  icon
                  small
                  :aria-label="`Visa ${translateDocumentType(doc.type)}`"
                  :loading="openingDocumentId === doc.id"
                  title="Visa"
                  @click="openDocument(doc)"
                >
                  <SvgIcon :path="mdiMagnify" :size="18" />
                </AppButton>
              </AppTableCell>
            </AppTableRow>
          </tbody>

          <tbody v-else>
            <AppTableRow>
              <AppTableCell colspan="3" class="text-center"> Inga dokument tillagda </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
      </div>

      <!-- Parter -->
      <div>
        <h3 :id="section.subSections.parties.id" class="mb-2">
          {{ section.subSections.parties.title }}
        </h3>
        <AppTable v-if="application.parties.length">
          <thead>
            <tr>
              <th>Namn</th>
              <th>Personnummer</th>
              <th>E-post</th>
              <th>Roll</th>
              <th>Signering</th>
            </tr>
          </thead>
          <tbody>
            <AppTableRow v-for="(party, index) in application.parties" :key="index">
              <AppTableCell>{{ party.name }}</AppTableCell>
              <AppTableCell nowrap>{{ party.personalNumber }}</AppTableCell>
              <AppTableCell>{{ party.email ?? '-' }}</AppTableCell>
              <AppTableCell>{{ $t(`PARTY_STATUS.${party.status}`) }}</AppTableCell>
              <AppTableCell>
                <a
                  v-if="party.scriveLink"
                  :href="party.scriveLink"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="scrive-link font-medium"
                >
                  Öppna
                </a>
                <span v-else>-</span>
              </AppTableCell>
            </AppTableRow>
          </tbody>
        </AppTable>
        <p v-else class="text-subtle">Saknar information om parter</p>
      </div>

      <!-- Syfte med utökning -->
      <div v-if="showTopupPurpose">
        <h3 :id="section.subSections.topupPurpose.id" class="mb-2">
          {{ section.subSections.topupPurpose.title }}
        </h3>
        <template v-if="application.topupPurpose">
          <p v-if="application.topupPurposeOther">{{ application.topupPurposeOther }}</p>
          <p v-else>{{ t(`PURPOSE.${application.topupPurpose}`) }}</p>
        </template>
        <p v-else class="text-subtle">Inget syfte angivet.</p>
      </div>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { mdiMagnify } from '@mdi/js'
import { formatAmountWithCurrency, formatDateIfValid, toNumericValue } from '@staff/shared'
import { AppButton, AppTable, AppTableCell, AppTableRow, SectionCard, SvgIcon, useModal } from '@staff/ui'
import type { ApplicationSections } from './sections'
import KalpModalContent from '../KalpModalContent.vue'
import { getApplicationLtvPercentage } from '@/utils/applicationDisplay'
import { useApplicationDocumentsContext, getDocumentsByType } from '@/views/application/applicationDocuments.context'
import { useDocumentPreview } from '@/composables/documentPreview'
import { useApplicationKalpContext, useRequiredApplication } from '@/views/application/application.context'
import { useMetadata } from '@/composables/useMetadata'

defineProps<{
  section: ApplicationSections['loan']
}>()

const { kalp, isKalpLoading, kalpError } = useApplicationKalpContext()
const { openModal } = useModal()
const application = useRequiredApplication()
const { translateDocumentType } = useMetadata()
const { t } = useI18n()

const showTopupPurpose = computed(() =>
  ['topupLoanApplication', 'transferLoanApplication'].includes(application.value.type),
)
const { documents } = useApplicationDocumentsContext()
const { openingDocumentId, openDocument } = useDocumentPreview((document) => translateDocumentType(document.type))

const amortizationProofs = computed(() => getDocumentsByType(documents.value, 'amortizationProof'))

const collateral = computed(() => application.value.collateralProperty)

const propertyValue = computed(() => {
  return toNumericValue(collateral.value?.valuations.currentValuation?.value)
})

const ltvPercentage = computed(() => {
  return getApplicationLtvPercentage(application.value)
})

const openKalpModal = () => {
  if (!kalp.value) return

  openModal(
    'Kvar att leva på kalkyl (KALP)',
    KalpModalContent,
    { application: application.value, kalp: kalp.value },
    { size: 'large' },
  )
}
</script>

<style scoped lang="scss">
.scrive-link {
  color: var(--color-brand-default);

  &:hover {
    color: var(--color-brand-emphasis);
    text-decoration: underline;
  }
}
</style>
